/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Flex,
    HStack,
    Text,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import usePromoCodeFields from '@salesforce/retail-react-app/app/components/forms/usePromoCodeFields'
import Field from '../../components/field/index'
import {DiscountIcon, PlusIcon} from '../icons'

const PromoCodeFields = ({form, prefix = '', ...props}) => {
    const fields = usePromoCodeFields({form, prefix})
    const styles = useMultiStyleConfig('OrderSummary', {})
    const code = form.watch('code')

    return (
        <Box {...styles.promo?.inputContainer} aria-labelledby="code-feedback" {...props}>
            <Accordion
                allowToggle
                sx={{'.chakra-collapse': {overflow: 'visible !important'}}}
                data-testid="promo-code-accordion"
            >
                <AccordionItem border="none" data-testid="promo-code-accordion-item">
                    {({isExpanded}) => (
                        <>
                            <AccordionButton {...styles.promo?.inputContainerHeader}>
                                <Flex {...styles.promo?.inputContainerHeaderContent}>
                                    <DiscountIcon {...styles.promo?.inputContainerHeaderIcon} />

                                    <Text {...styles.promo?.inputContainerHeaderText}>
                                        <FormattedMessage
                                            id="use_promo_code_fields.label.promo_code"
                                            defaultMessage="Promo Code"
                                        />
                                    </Text>
                                </Flex>

                                {!isExpanded ? (
                                    <Button
                                        variant="ghost-not-rounded"
                                        size="small"
                                        {...styles.promo?.inputAccordionButton}
                                    >
                                        <Text {...styles.promo?.inputAccordionButtonText}>
                                            <FormattedMessage
                                                id="use_promo_code_fields.label.add_button"
                                                defaultMessage="Add"
                                            />
                                        </Text>

                                        <PlusIcon {...styles.promo?.inputAccordionButtonIcon} />
                                    </Button>
                                ) : (
                                    <Button
                                        variant="link-button"
                                        {...styles.promo?.inputAccordionButtonCancel}
                                    >
                                        <Text {...styles.promo?.inputAccordionButtonText}>
                                            <FormattedMessage
                                                id="use_promo_code_fields.label.cancel_button"
                                                defaultMessage="Cancel"
                                            />
                                        </Text>
                                    </Button>
                                )}
                            </AccordionButton>

                            <AccordionPanel
                                {...styles.promo?.inputContainerPanel}
                                data-testid="promo-code-accordion-panel"
                            >
                                <HStack
                                    {...styles.promo?.inputAndButtonContainer}
                                    alignItems="flex-start"
                                >
                                    <Field
                                        {...fields.code}
                                        shouldDisplaySuccess={false}
                                        enableMcpContainer={true}
                                        size="xl"
                                    />

                                    <Button
                                        type="submit"
                                        isLoading={form.formState.isSubmitting}
                                        isDisabled={(code?.trim()?.length ?? 0) < 1}
                                        variant="primary-not-rounded"
                                        size="large"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Apply"
                                            id="promo_code_fields.button.apply"
                                        />
                                    </Button>
                                </HStack>

                                {/* Out of scope for now -- Codes For You functionality */}

                                {/* <Accordion allowToggle {...styles.promo?.inputCodesForYouAccordion}>
                                    <AccordionItem border="none">
                                        <AccordionButton
                                            {...styles.promo?.inputCodesForYouAccordionHeader}
                                        >
                                            <Text
                                                flex="1"
                                                textAlign="left"
                                                {...styles.promo?.inputCodesForYouTitle}
                                            >
                                                Codes for you
                                            </Text>

                                            <AccordionIcon />
                                        </AccordionButton>

                                        <AccordionPanel px={0}>
                                            <Flex {...styles.promo?.inputCodesForYouContainer}>
                                                <Flex
                                                    {...styles.promo?.inputCodesForYouTextContainer}
                                                >
                                                    <Text {...styles.promo?.inputCodeTitle}>
                                                        Welcome
                                                    </Text>

                                                    <Text {...styles.promo?.inputCodeDescription}>
                                                        20% on your first order
                                                    </Text>
                                                </Flex>

                                                <Button
                                                    variant="link-button"
                                                    size="small"
                                                    {...styles.promo?.inputCodeButton}
                                                >
                                                    <Text {...styles.promo?.inputCodeButtonText}>
                                                        <FormattedMessage
                                                            defaultMessage="Apply"
                                                            id="promo_code_fields.button.apply"
                                                        />
                                                    </Text>
                                                </Button>
                                            </Flex>
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Accordion> */}
                            </AccordionPanel>
                        </>
                    )}
                </AccordionItem>
            </Accordion>
        </Box>
    )
}

PromoCodeFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,
}

export default PromoCodeFields
